import {Action, getModule, Module} from 'vuex-module-decorators';
import store from '@/store';
import {DtcpEmulatorModule, PModuleMeasurement} from '@/views/system/modules/interfaces';
import {PModuleType} from '@/enums/configs';
import {
  PMProcessActionType,
  PMProcessStatus,
  PMProcessSystemType,
} from '@/views/system/modules/enums';
import {EntityModule} from '@/store/modules/entities/entityModule';
import {httpClient} from '@/api/baseApi';
import {PModuleStoreModule} from '@/store/modules/entities/pmodules/interfaces';
import {DateTimeInterval} from '@/utils/dateTimeItervalUtils';
import {
  getEventData,
  getEventTypes,
  getLastEvents,
  getSignal,
} from '@/store/modules/entities/pmodules/base';
import {isNumber} from '@/utils/commonUtils';

const moduleName = 'dtcpEmulatorStoreModule';

function transformGet(elem: any): DtcpEmulatorModule {
  return {
    id: elem.id,
    guid: elem.guid,
    name: elem.name,
    description: elem.description,
    processStatus: elem.process_status as PMProcessStatus,
    processStatusDescription: elem.process_status_description,
    status: elem.status,
    nodes: elem.nodes,
    alias: elem.alias,
    type: PModuleType.DtcpEmulatorModule,
    temperatureModules: elem.temperature_modules,
    energyModules: elem.energy_modules,
    tahoModules: elem.taho_modules,
    accelerometerModules: elem.accelerometer_modules,
    measurements: [],
  };
}

function getMeasurements(elem: any): PModuleMeasurement[] {
  return elem.measurements.map((elem: any) => ({
    measurementId: elem.module_type_measurement_id,
    measurementDescription: elem.module_type_measurement_description,
    topWarningBoundary: elem.top_warning_boundary,
    topDangerBoundary: elem.top_danger_boundary,
    topErrorBoundary: elem.top_error_boundary,
    bottomWarningBoundary: elem.bottom_warning_boundary,
    bottomDangerBoundary: elem.bottom_danger_boundary,
    bottomErrorBoundary: elem.bottom_error_boundary,
  }));
}

function transformGetById(elem: any): DtcpEmulatorModule {
  const model = transformGet(elem);
  model.measurements = getMeasurements(elem);
  return model;
}

function transformCreateEdit(elem: any): DtcpEmulatorModule {
  const measurements: PModuleMeasurement[] = getMeasurements(elem);

  return {
    name: elem.name,
    alias: elem.alias,
    guid: elem.guid,
    type: PModuleType.DtcpEmulatorModule,
    description: elem.description,
    nodes: elem.nodes,
    temperatureModules: elem.temperature_modules,
    energyModules: elem.energy_modules,
    tahoModules: elem.taho_modules,
    accelerometerModules: elem.accelerometer_modules,
    measurements: measurements,
  };
}

function transformSave(model: DtcpEmulatorModule): any {
  const measurements: any[] = model.measurements.map((elem: any) => {
    const m: any = {
      module_type_measurement_id: elem.measurementId,
      module_type_measurement_description: elem.measurementDescription,
    };
    if (isNumber(elem.topWarningBoundary)) m.top_warning_boundary = elem.topWarningBoundary;
    if (isNumber(elem.topDangerBoundary)) m.top_danger_boundary = elem.topDangerBoundary;
    if (isNumber(elem.topErrorBoundary)) m.top_error_boundary = elem.topErrorBoundary;
    if (isNumber(elem.bottomWarningBoundary))
      m.bottom_warning_boundary = elem.bottomWarningBoundary;
    if (isNumber(elem.bottomDangerBoundary)) m.bottom_danger_boundary = elem.bottomDangerBoundary;
    if (isNumber(elem.bottomErrorBoundary)) m.bottom_error_boundary = elem.bottomErrorBoundary;
    return m;
  });

  return {
    name: model.name,
    description: model.description,
    nodes: model.nodes,
    temperature_modules: model.temperatureModules,
    energy_modules: model.energyModules,
    taho_modules: model.tahoModules,
    accelerometer_modules: model.accelerometerModules,
    measurements: measurements,
  };
}

@Module({
  name: moduleName,
  store: store,
  dynamic: true,
  namespaced: true,
})
export class DtcpEmulatorStoreModule extends EntityModule implements PModuleStoreModule {
  protected routeName = 'dtcp-emulator-modules';

  get all(): DtcpEmulatorModule[] {
    return super.all as DtcpEmulatorModule[];
  }

  //Получение списка объектов с сервера
  @Action
  public async fetchAll() {
    return super.fetchAll(transformGet);
  }

  //Получение модели для создания объекта
  @Action
  public async create(): Promise<{data: DtcpEmulatorModule}> {
    return super.create(transformCreateEdit) as Promise<{data: DtcpEmulatorModule}>;
  }

  //Получение модели для редактирования объекта
  @Action
  public async edit(id: any): Promise<{data: DtcpEmulatorModule}> {
    return super.edit(id, transformCreateEdit) as Promise<{data: DtcpEmulatorModule}>;
  }

  //Создание/реактирование объекта
  @Action
  public async save(model: DtcpEmulatorModule) {
    return super.save(model, transformSave);
  }

  //Получение объекта по идентификатору
  @Action
  async getById(id: any): Promise<{data: DtcpEmulatorModule}> {
    return super.getById(id, transformGetById) as Promise<{data: DtcpEmulatorModule}>;
  }

  /* TODO: Данные методы не вынести в базовый класс (т. к. в store наследование больше 2хмодулей не работает) */

  //Получение сигнала
  @Action
  async getSignal(params: {
    id: number;
    dateInterval: DateTimeInterval;
    fieldAlias: string;
  }): Promise<any> {
    try {
      return await getSignal(this.routeName, params);
    } catch (e) {
      return Promise.reject(e);
    }
  }

  //Выполнить действие
  @Action
  async processAction(params: {id: number; action: PMProcessActionType}) {
    const {data} = await httpClient.put(`/${this.routeName}/${params.id}/processes/`, {
      action: params.action,
    });
    return data;
  }

  //Выполнить системную команду программного модуля
  @Action
  async processSystem(params: {id: number; action: PMProcessSystemType}) {
    const {data} = await httpClient.put(`/${this.routeName}/${params.id}/processes/system`, {
      action: params.action,
    });
    return data;
  }

  //Получение типов событий
  @Action
  async getEventTypes(): Promise<any> {
    try {
      return await getEventTypes(this.routeName);
    } catch (e) {
      return Promise.reject(e);
    }
  }

  //Получение списка последних событий
  @Action
  async getLastEvents(params: {id: number; eventType: string; limit?: number}): Promise<any> {
    try {
      return await getLastEvents(this.routeName, params);
    } catch (e) {
      return Promise.reject(e);
    }
  }

  //Получение данных события
  @Action
  async getEventData(params: {id: number; eventType: string; timestamp: number}): Promise<any> {
    try {
      return await getEventData(this.routeName, params);
    } catch (e) {
      return Promise.reject(e);
    }
  }
}

export default getModule(DtcpEmulatorStoreModule);
